import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { getLscMemberTeamOptions } from '../api';
import type { LscMember, LscMemberTeamOption } from '../types';
import {
  formatTeamOptionLabel,
  getMemberTeamAssignmentState,
  getTeamAssignmentConflictLabels,
  isTeamOptionSelectable,
  memberFormValuesFromMember,
  memberHasTeamConflict,
  validateCustomTeamName,
  type MemberFormValues,
  type TeamAssignmentMode,
} from '../utils/memberTeamAssignment';
import { streamPlatformFieldsFromMember } from '../utils/memberStatus';

export type { MemberFormValues, TeamAssignmentMode };

const EMPTY: MemberFormValues = {
  display_name: '',
  role: 'member',
  status: 'active',
  user_uid: '',
  twitch_login: '',
  twitch_user_id: '',
  current_side_label: '',
  team_assignment_mode: 'unassigned',
  team_assignment_is_custom: false,
  team_assignment_source_team_id: '',
  custom_team_name: '',
  resolve_team_conflict: '',
  stream_platform: 'twitch',
  youtube_channel: '',
};

const TEAM_SELECT_UNASSIGNED = '__unassigned__';
const TEAM_SELECT_CUSTOM = '__custom__';

export interface MemberTeamAssignmentFieldsProps {
  leagueId?: string;
  memberId?: string;
  values: MemberFormValues;
  initial?: LscMember | null;
  saving: boolean;
  formError?: string | null;
  onChange: (values: MemberFormValues) => void;
  onConflictResolve: (resolution: MemberFormValues['resolve_team_conflict']) => void;
}

export function MemberTeamAssignmentFields({
  leagueId,
  memberId,
  values,
  initial,
  saving,
  formError,
  onChange,
  onConflictResolve,
}: MemberTeamAssignmentFieldsProps) {
  const [teamOptions, setTeamOptions] = useState<LscMemberTeamOption[]>([]);
  const [teamOptionsLoading, setTeamOptionsLoading] = useState(false);
  const [teamOptionsError, setTeamOptionsError] = useState<string | null>(null);
  const [unclaimedOnly, setUnclaimedOnly] = useState(false);
  const [customValidationError, setCustomValidationError] = useState<string | null>(null);

  const loadTeamOptions = useCallback(async () => {
    if (!leagueId || !memberId) {
      setTeamOptions([]);
      return;
    }
    setTeamOptionsLoading(true);
    setTeamOptionsError(null);
    try {
      const result = await getLscMemberTeamOptions(leagueId, {
        current_member_id: memberId,
        unclaimed_only: unclaimedOnly,
      });
      setTeamOptions(result.teams ?? []);
    } catch {
      setTeamOptions([]);
      setTeamOptionsError('Team options are unavailable right now.');
    } finally {
      setTeamOptionsLoading(false);
    }
  }, [leagueId, memberId, unclaimedOnly]);

  useEffect(() => {
    void loadTeamOptions();
  }, [loadTeamOptions]);

  const selectValue = useMemo(() => {
    if (values.team_assignment_mode === 'unassigned') {
      return TEAM_SELECT_UNASSIGNED;
    }
    if (values.team_assignment_mode === 'custom') {
      return TEAM_SELECT_CUSTOM;
    }
    return values.current_side_label || TEAM_SELECT_UNASSIGNED;
  }, [values.team_assignment_mode, values.current_side_label]);

  const showConflictBanner = memberHasTeamConflict(initial);
  const assignmentState = getMemberTeamAssignmentState(initial);
  const showCustomDivergenceInfo = assignmentState === 'custom_external_divergence';
  const conflictLabels = getTeamAssignmentConflictLabels(initial);
  const externalTeamName = conflictLabels.externalLabel;
  const lscTeamLabel = conflictLabels.lscLabel ?? initial?.current_side_label ?? 'Unassigned';
  const teamControlsDisabled = saving || teamOptionsLoading || Boolean(teamOptionsError);

  const handleTeamSelectChange = (nextValue: string) => {
    setCustomValidationError(null);
    if (nextValue === TEAM_SELECT_UNASSIGNED) {
      onChange({
        ...values,
        team_assignment_mode: 'unassigned',
        team_assignment_is_custom: false,
        current_side_label: '',
        team_assignment_source_team_id: '',
        custom_team_name: '',
        resolve_team_conflict: '',
      });
      return;
    }
    if (nextValue === TEAM_SELECT_CUSTOM) {
      onChange({
        ...values,
        team_assignment_mode: 'custom',
        team_assignment_is_custom: true,
        team_assignment_source_team_id: '',
        resolve_team_conflict: '',
      });
      return;
    }
    const option = teamOptions.find((team) => team.label === nextValue);
    onChange({
      ...values,
      team_assignment_mode: 'existing',
      team_assignment_is_custom: false,
      current_side_label: nextValue,
      team_assignment_source_team_id: option?.source_team_id ?? '',
      custom_team_name: '',
      resolve_team_conflict: '',
    });
  };

  const handleCustomNameChange = (nextValue: string) => {
    setCustomValidationError(validateCustomTeamName(nextValue));
    onChange({
      ...values,
      custom_team_name: nextValue,
      team_assignment_mode: 'custom',
      team_assignment_is_custom: true,
      resolve_team_conflict: '',
    });
  };

  return (
    <fieldset
      className="space-y-3 rounded-lg border border-secondary-700 p-3"
      data-testid="member-team-assignment-section"
      disabled={saving}
    >
      <legend className="px-1 text-sm font-medium text-white">Team Assignment</legend>

      {showCustomDivergenceInfo && initial ? (
        <div
          className="rounded-md border border-secondary-700 bg-secondary-900/50 p-3 text-sm text-secondary-200 space-y-1"
          data-testid="member-custom-divergence-banner"
        >
          <p className="font-medium">Custom team assignment</p>
          <p>
            LSC uses your custom team name ({lscTeamLabel}). External source shows:{' '}
            {externalTeamName ?? 'Unknown'}.
          </p>
        </div>
      ) : null}

      {showConflictBanner && initial ? (
        <div
          className="rounded-md border border-amber-700 bg-amber-950/40 p-3 text-sm text-amber-100 space-y-2"
          data-testid="member-team-conflict-banner"
        >
          <p className="font-medium">Team assignment conflict</p>
          <p>LSC: {lscTeamLabel}</p>
          <p>
            LSC is keeping the current team assignment until you choose a resolution. External
            source shows: {externalTeamName ?? 'Unknown'}.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={saving}
              onClick={() => onConflictResolve('keep_lsc')}
              data-testid="team-conflict-keep-lsc"
            >
              Keep LSC
            </button>
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={saving || !externalTeamName}
              onClick={() => onConflictResolve('accept_external')}
              data-testid="team-conflict-accept-external"
            >
              Accept External
            </button>
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={saving}
              onClick={() => onConflictResolve('custom')}
              data-testid="team-conflict-use-custom"
            >
              Use Custom
            </button>
          </div>
        </div>
      ) : null}

      {teamOptionsError ? (
        <p className="text-sm text-amber-300" data-testid="member-team-options-error">
          {teamOptionsError}
        </p>
      ) : null}

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-secondary-400">Team</span>
        <select
          className="input-field"
          value={selectValue}
          onChange={(e) => handleTeamSelectChange(e.target.value)}
          disabled={teamControlsDisabled}
          data-testid="member-team-select"
        >
          <option value={TEAM_SELECT_UNASSIGNED}>Unassigned</option>
          {teamOptions.map((option) => {
            const selectable = isTeamOptionSelectable(option, memberId);
            return (
              <option
                key={`${option.label}-${option.source_team_id ?? 'none'}`}
                value={option.label}
                disabled={!selectable}
              >
                {formatTeamOptionLabel(option)}
                {!selectable ? ' (claimed)' : ''}
              </option>
            );
          })}
          <option value={TEAM_SELECT_CUSTOM}>Custom / Relocated team</option>
        </select>
      </label>

      <label className="flex items-center gap-2 text-sm text-secondary-300">
        <input
          type="checkbox"
          checked={unclaimedOnly}
          onChange={(e) => setUnclaimedOnly(e.target.checked)}
          disabled={teamControlsDisabled}
          data-testid="member-team-unclaimed-only"
        />
        <span>Show only unclaimed teams</span>
      </label>

      {values.team_assignment_mode === 'custom' ? (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary-400">Custom team name</span>
          <input
            className="input-field"
            value={values.custom_team_name}
            onChange={(e) => handleCustomNameChange(e.target.value)}
            maxLength={64}
            disabled={teamControlsDisabled}
            data-testid="member-custom-team-name"
          />
          {customValidationError ? (
            <span className="text-xs text-red-300" data-testid="member-custom-team-error">
              {customValidationError}
            </span>
          ) : null}
        </label>
      ) : null}

      {formError ? (
        <p className="text-sm text-red-300" data-testid="member-team-form-error">
          {formError}
        </p>
      ) : null}
    </fieldset>
  );
}

interface MemberFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  leagueId?: string;
  initial?: LscMember | null;
  saving: boolean;
  formError?: string | null;
  onClose: () => void;
  onSubmit: (values: MemberFormValues) => Promise<void>;
}

const MemberFormModal: React.FC<MemberFormModalProps> = ({
  open,
  mode,
  leagueId,
  initial,
  saving,
  formError,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<MemberFormValues>(EMPTY);
  const [youtubeChannelError, setYoutubeChannelError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setValues({
        display_name: initial.display_name ?? '',
        role: initial.role ?? 'member',
        status: initial.status ?? 'active',
        user_uid: initial.user_uid ?? '',
        twitch_login: initial.twitch_login ?? '',
        twitch_user_id: initial.twitch_user_id ?? '',
        ...memberFormValuesFromMember(initial),
        ...streamPlatformFieldsFromMember(initial),
      });
      setYoutubeChannelError(null);
    } else {
      setValues(EMPTY);
      setYoutubeChannelError(null);
    }
  }, [open, mode, initial]);

  if (!open) return null;

  const title = mode === 'create' ? 'Add member' : 'Edit member';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (values.team_assignment_mode === 'custom') {
      const validationError = validateCustomTeamName(values.custom_team_name);
      if (validationError) {
        return;
      }
    }
    if (mode === 'edit' && values.stream_platform === 'youtube' && !values.youtube_channel.trim()) {
      setYoutubeChannelError('Enter a YouTube channel URL or @handle.');
      return;
    }
    setYoutubeChannelError(null);
    await onSubmit(values);
  };

  const handleConflictResolve = (resolution: MemberFormValues['resolve_team_conflict']) => {
    if (!resolution) return;
    if (resolution === 'custom') {
      setValues((current) => ({
        ...current,
        team_assignment_mode: 'custom',
        team_assignment_is_custom: true,
        resolve_team_conflict: 'custom',
      }));
      return;
    }
    void onSubmit({
      ...values,
      resolve_team_conflict: resolution,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-form-title"
      data-testid="member-form-modal"
    >
      <div className="card max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-secondary-400 hover:text-white"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 id="member-form-title" className="text-lg font-semibold text-white mb-4">
          {title}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Display name</span>
            <input
              className="input-field"
              value={values.display_name}
              onChange={(e) => setValues((v) => ({ ...v, display_name: e.target.value }))}
              required
              disabled={saving}
            />
          </label>


          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Status</span>
            <select
              className="input-field"
              value={values.status}
              onChange={(e) => setValues((v) => ({ ...v, status: e.target.value }))}
              disabled={saving}
            >
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>

          {mode === 'edit' && initial ? (
            <MemberTeamAssignmentFields
              leagueId={leagueId}
              memberId={initial.id}
              values={values}
              initial={initial}
              saving={saving}
              formError={formError}
              onChange={setValues}
              onConflictResolve={handleConflictResolve}
            />
          ) : null}

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Linked user UID</span>
            <input
              className="input-field font-mono text-xs"
              value={values.user_uid}
              onChange={(e) => setValues((v) => ({ ...v, user_uid: e.target.value }))}
              disabled={saving}
              placeholder="Optional Firebase user id"
            />
          </label>

          {mode === 'edit' && initial?.google_email ? (
            <div className="flex flex-col gap-1 text-sm" data-testid="member-google-email">
              <span className="text-secondary-400">Google account</span>
              <span className="text-white">{initial.google_email}</span>
            </div>
          ) : null}

          {mode === 'edit' ? (
            <>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-secondary-400">Stream platform</span>
                <select
                  className="input-field"
                  value={values.stream_platform}
                  onChange={(e) =>
                    setValues((v) => ({
                      ...v,
                      stream_platform: e.target.value === 'youtube' ? 'youtube' : 'twitch',
                    }))
                  }
                  disabled={saving}
                  data-testid="member-stream-platform"
                >
                  <option value="twitch">Twitch</option>
                  <option value="youtube">YouTube</option>
                </select>
              </label>
              {values.stream_platform === 'youtube' ? (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-secondary-400">YouTube channel</span>
                  <input
                    className="input-field"
                    value={values.youtube_channel}
                    onChange={(e) => {
                      setYoutubeChannelError(null);
                      setValues((v) => ({ ...v, youtube_channel: e.target.value }));
                    }}
                    disabled={saving}
                    placeholder="@handle or channel URL"
                    data-testid="member-youtube-channel"
                  />
                  {youtubeChannelError ? (
                    <span className="text-xs text-red-300" data-testid="member-youtube-channel-error">
                      {youtubeChannelError}
                    </span>
                  ) : (
                    <span className="text-xs text-secondary-500">
                      Saving YouTube revokes the current claim link. Copy a new one afterward — it asks them to sign in with Google.
                    </span>
                  )}
                </label>
              ) : null}
            </>
          ) : null}

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Stream account (Twitch) — Twitch login</span>
            <input
              className="input-field"
              value={values.twitch_login}
              onChange={(e) => setValues((v) => ({ ...v, twitch_login: e.target.value }))}
              disabled={saving}
              placeholder="e.g. cpg_twon"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Twitch user ID</span>
            <input
              className="input-field font-mono text-xs"
              value={values.twitch_user_id}
              onChange={(e) => setValues((v) => ({ ...v, twitch_user_id: e.target.value }))}
              disabled={saving}
              placeholder="Optional numeric Twitch user id"
            />
          </label>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Add member' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberFormModal;
